import React, { useEffect, useMemo, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { useFormikContext } from "formik";
import { useLocation } from "wouter";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Loader } from "@/components/Common/Loader/Loader";
import { ConfirmModal } from "@/components/Common/ConfirmModal/ConfirmModal";
import { SavedInConfigMarker } from "@/components/ClustersPage/SavedDeploymentConfigs/SavedInConfigMarker";
import { AddSshKeyDialog } from "@/components/Account/AccountSettings/SshKeys/AddSshKeyDialog";
import { SshKeyListItem } from "@/components/Account/AccountSettings/SshKeys/SshKeyListItem";
import { useSavedSshKeys } from "@/hooks/useSavedSshKeys";
import { useAuthState } from "@/store/auth";
import { useUserStore } from "@/store/user";
import { useToast } from "@/components/ui/use-toast";
import { SimpleClusterForm } from "@/types";
import { SavedSshKey } from "@/types/savedSshKey";
import { explorerPrefixedPath } from "@/utils";
import { getToken } from "@/utils/api";
import localCookies from "@/utils/cookie";
import { sanitizeRedirectUrl } from "@/utils/sanitize";
import { normalizePublicKey } from "@/utils/sshKeyFingerprint";
import { AnalyticsEventType } from "@/constants/analytics";
import { trackEvent } from "@/utils/analytics";

const MAX_DEPLOY_KEYS = 10;

const accountKeysToFormKeys = (keys: SavedSshKey[], selectedIds: string[]) => {
  const selected = new Set(selectedIds);
  return keys
    .filter((key) => selected.has(key.id))
    .slice(0, MAX_DEPLOY_KEYS)
    .map((key) => ({
      id: key.id,
      key: key.name,
      value: key.publicKey
    }));
};

const resolveLegacySelection = (
  accountKeys: SavedSshKey[],
  legacySshKeys: SimpleClusterForm["sshKeys"]
): string[] => {
  if (!legacySshKeys?.length) return [];
  const ids: string[] = [];
  legacySshKeys.forEach((legacy) => {
    const normalized = normalizePublicKey(legacy.value ?? "");
    const match = accountKeys.find(
      (key) => normalizePublicKey(key.publicKey) === normalized && normalized
    );
    if (match) ids.push(match.id);
  });
  return ids;
};

export const SavedSshKeysDeploySection: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<SimpleClusterForm>();
  const { toast } = useToast();
  const { keys, isLoading, error, addKey, refresh, removeKey } = useSavedSshKeys();
  const [addOpen, setAddOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const hasInitializedSelectionRef = useRef(false);
  const navigateToSettingsAfterLoginRef = useRef(false);
  const openAddKeyAfterLoginRef = useRef(false);
  const [, setLocation] = useLocation();
  const isLoggedIn = useAuthState((state) => state.status === "authenticated");
  const canManageKeys = isLoggedIn && Boolean(getToken());
  const isRequiresLoginModalOpen = useUserStore((state) => state.isRequiresLoginModalOpen);
  const setRequiresLoginModalOpen = useUserStore((state) => state.setRequiresLoginModalOpen);
  const setRequiresLoginMode = useUserStore((state) => state.setRequiresLoginMode);
  const accountSettingsPath = explorerPrefixedPath("/id/account/settings");

  const selectedIds = values.selectedSshKeyIds ?? [];

  useEffect(() => {
    if (!canManageKeys) return;

    if (navigateToSettingsAfterLoginRef.current) {
      navigateToSettingsAfterLoginRef.current = false;
      setLocation(accountSettingsPath);
      return;
    }

    if (openAddKeyAfterLoginRef.current) {
      openAddKeyAfterLoginRef.current = false;
      setAddOpen(true);
    }
  }, [accountSettingsPath, canManageKeys, setLocation]);

  useEffect(() => {
    if (!isRequiresLoginModalOpen && !canManageKeys) {
      navigateToSettingsAfterLoginRef.current = false;
      openAddKeyAfterLoginRef.current = false;
    }
  }, [canManageKeys, isRequiresLoginModalOpen]);

  const requireLogin = () => {
    setRequiresLoginMode("default");
    setRequiresLoginModalOpen(true);
  };

  const handleManageKeysClick = () => {
    if (canManageKeys) {
      setLocation(accountSettingsPath);
      return;
    }

    navigateToSettingsAfterLoginRef.current = true;
    localCookies.setItem("LOGIN_REDIRECT_ROUTE", sanitizeRedirectUrl(accountSettingsPath));
    requireLogin();
  };

  const handleAddKeyClick = () => {
    if (canManageKeys) {
      setAddOpen(true);
      return;
    }

    openAddKeyAfterLoginRef.current = true;
    requireLogin();
  };

  // Initialize selection once when account keys load; afterward only sync sshKeys.
  // Never re-default to "all selected" after the user has toggled checkboxes.
  useEffect(() => {
    if (isLoading || keys.length === 0) return;

    if (!hasInitializedSelectionRef.current) {
      hasInitializedSelectionRef.current = true;

      const existing = values.selectedSshKeyIds;
      const validExisting = Array.isArray(existing)
        ? existing.filter((id) => keys.some((key) => key.id === id))
        : [];

      let nextSelected: string[];
      if (validExisting.length > 0) {
        nextSelected = validExisting;
      } else {
        const legacyIds = resolveLegacySelection(keys, values.sshKeys);
        nextSelected = legacyIds.length > 0 ? legacyIds : keys.map((key) => key.id);
      }

      setFieldValue("selectedSshKeyIds", nextSelected, false);
      setFieldValue("sshKeys", accountKeysToFormKeys(keys, nextSelected), false);
      setFieldValue("sshType", "manual", false);
      return;
    }

    setFieldValue(
      "sshKeys",
      accountKeysToFormKeys(keys, values.selectedSshKeyIds ?? []),
      false
    );
  }, [isLoading, keys, setFieldValue, values.selectedSshKeyIds]);

  const allSelected = useMemo(
    () => keys.length > 0 && keys.every((key) => selectedIds.includes(key.id)),
    [keys, selectedIds]
  );

  const toggleKey = (keyId: string, checked: boolean) => {
    const next = checked
      ? [...new Set([...selectedIds, keyId])].slice(0, MAX_DEPLOY_KEYS)
      : selectedIds.filter((id) => id !== keyId);
    setFieldValue("selectedSshKeyIds", next, false);
    setFieldValue("sshKeys", accountKeysToFormKeys(keys, next), false);
    trackEvent(AnalyticsEventType.SAVED_SSH_KEY_DEPLOY_TOGGLED, { checked });
  };

  const handleAddKey = async (input: { name: string; publicKey: string }) => {
    const created = await addKey(input);
    await refresh();
    const nextSelected = [...selectedIds, created.id].slice(0, MAX_DEPLOY_KEYS);
    setFieldValue("selectedSshKeyIds", nextSelected, false);
    setFieldValue("sshKeys", accountKeysToFormKeys([created, ...keys], nextSelected), false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const idToDelete = deleteId;
    try {
      await removeKey(idToDelete);
      const nextSelected = selectedIds.filter((id) => id !== idToDelete);
      setFieldValue("selectedSshKeyIds", nextSelected, false);
      setFieldValue(
        "sshKeys",
        accountKeysToFormKeys(
          keys.filter((key) => key.id !== idToDelete),
          nextSelected
        ),
        false
      );
      trackEvent(AnalyticsEventType.SAVED_SSH_KEY_DELETED, {});
      toast({ title: "SSH key deleted" });
    } catch (e) {
      toast({
        title: "Failed to delete SSH key",
        description: e instanceof Error ? e.message : undefined,
        variant: "error"
      });
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div id="ssh-keys-configuration" className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h4 className="flex items-center gap-2 text-[16px] font-medium leading-[24px] text-foreground">
          SSH Keys
          <SavedInConfigMarker />
        </h4>
        <p className="text-[14px] leading-[20px] text-muted-foreground">
          Select which saved SSH keys to add to this VM.
        </p>
      </div>

      <div className="rounded-lg border border-blue-light-300/30 bg-blue-light-300/10 px-4 py-3 text-sm text-foreground">
        SSH keys are automatically added to new VM instances at launch time.
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader className="h-[40px] w-[40px]" />
        </div>
      ) : error ? (
        <p className="text-sm text-red-500">{error}</p>
      ) : keys.length === 0 ? (
        <div className="rounded-lg border border-border bg-background p-4">
          <p className="text-sm text-muted-foreground">
            No SSH keys in your account yet. Add a key to include it on this VM.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {keys.map((key) => {
            const checked = selectedIds.includes(key.id);
            return (
              <SshKeyListItem
                key={key.id}
                sshKey={key}
                onDelete={setDeleteId}
                leading={
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(value) => toggleKey(key.id, value === true)}
                    aria-label={`Include ${key.name} on this VM`}
                  />
                }
              />
            );
          })}
          {!allSelected && selectedIds.length >= MAX_DEPLOY_KEYS ? (
            <p className="text-xs text-muted-foreground">
              Maximum of {MAX_DEPLOY_KEYS} SSH keys per deployment.
            </p>
          ) : null}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={handleAddKeyClick}
        >
          <Plus className="h-4 w-4" />
          Add key
        </Button>
        <button
          type="button"
          onClick={handleManageKeysClick}
          className="text-sm text-primary underline-offset-4 hover:underline"
        >
          Manage keys in Account Settings →
        </button>
      </div>

      <AddSshKeyDialog isOpen={addOpen} onOpenChange={setAddOpen} onSubmit={handleAddKey} />

      <ConfirmModal
        isOpen={Boolean(deleteId)}
        title="Delete SSH key?"
        description="This key will be removed from your account. Existing VMs are not affected."
        submitLabel="Delete"
        submitButtonVariant="error"
        onClose={() => setDeleteId(null)}
        onSubmit={() => void handleDelete()}
      />
    </div>
  );
};
